import LegalLayout from "../components/LegalLayout";

const sections = [
  { id: "scope", number: "01", label: "Policy Scope" },
  { id: "eligibility", number: "02", label: "Refund Eligibility" },
  { id: "non-refundable", number: "03", label: "Non-Refundable Cases" },
  { id: "request", number: "04", label: "How to Request a Refund" },
  { id: "processing", number: "05", label: "Processing Timeline" },
  { id: "duplicate", number: "06", label: "Duplicate Payments" },
  { id: "cancellation", number: "07", label: "CODEION Cancellations" },
  { id: "discounts", number: "08", label: "Discounted Enrollments" },
  { id: "chargebacks", number: "09", label: "Payment Disputes" },
  { id: "decision", number: "10", label: "Review & Decision" },
  { id: "contact", number: "11", label: "Contact Us" },
];

function Section({ id, number, title, children }) {
  return (
    <section id={id} className="legal-section">
      <div className="legal-section-heading">
        <span className="legal-section-number">{number}</span>
        <h2>{title}</h2>
      </div>

      {children}
    </section>
  );
}

function RefundPolicy() {
  return (
    <LegalLayout
      title="Refund Policy"
      description="This Refund Policy explains when a CODEION enrollment may qualify for a refund, when refunds are not available, how to submit a request, and how approved refunds are processed."
      lastUpdated="July 2026"
      sections={sections}
    >
      <div className="legal-intro">
        CODEION provides digital educational services that may include live
        classes, recorded resources, mentorship, projects, assignments, and
        access to learning platforms. Because access to these services may begin
        shortly after enrollment, refund requests are subject to the conditions
        described below.
      </div>

      <Section id="scope" number="01" title="Scope of This Policy">
        <p>
          This Refund Policy applies to eligible payments made directly for
          CODEION programs and services through payment methods or payment
          channels authorized by CODEION.
        </p>

        <p>
          Refund requests for purchases made through an independent third-party
          marketplace or service may also be subject to that provider's refund
          procedures.
        </p>
      </Section>

      <Section id="eligibility" number="02" title="Eligible Refund Requests">
        <p>
          A refund request may be considered when all applicable eligibility
          conditions are satisfied.
        </p>

        <ul>
          <li>The request is submitted within 5 calendar days of successful payment.</li>
          <li>The learner has not attended more than 2 live classes.</li>
          <li>The learner has not substantially consumed or accessed the course content.</li>
          <li>Course resources have not been copied, misused, shared, or redistributed.</li>
          <li>Valid payment and enrollment information is provided.</li>
          <li>The request is submitted through CODEION's official support channel.</li>
        </ul>

        <div className="legal-notice">
          Meeting the basic eligibility conditions does not automatically approve
          a refund. Each request may be reviewed to verify payment, enrollment,
          attendance, content access, and other relevant circumstances.
        </div>
      </Section>

      <Section id="non-refundable" number="03" title="Non-Refundable Cases">
        <p>A refund may be declined in circumstances including:</p>

        <ul>
          <li>The refund request is submitted after the applicable refund window.</li>
          <li>The learner has attended more than the permitted number of live classes.</li>
          <li>A substantial portion of the program or digital content has already been accessed.</li>
          <li>The program has been completed or a completion certificate has been issued.</li>
          <li>Course materials have been copied, shared, downloaded without authorization, or redistributed.</li>
          <li>The account is associated with fraud, abuse, piracy, payment misuse, or dishonest activity.</li>
          <li>The learner was removed from a program because of serious misconduct or repeated policy violations.</li>
        </ul>
      </Section>

      <Section id="request" number="04" title="How to Request a Refund">
        <p>
          Refund requests must be sent to the CODEION support team using the
          official support email address.
        </p>

        <p>Please include:</p>

        <ul>
          <li>Your full name.</li>
          <li>Email address used for enrollment.</li>
          <li>Registered phone number, if applicable.</li>
          <li>Course or program name.</li>
          <li>Payment ID, transaction ID, or order reference.</li>
          <li>Date of payment.</li>
          <li>A clear explanation of the reason for requesting a refund.</li>
        </ul>

        <p>
          Requests with incomplete information may require additional
          verification and may take longer to review.
        </p>
      </Section>

      <Section id="processing" number="05" title="Refund Processing Timeline">
        <p>
          CODEION will review eligible refund requests after receiving the
          information necessary to verify the request.
        </p>

        <p>
          When a refund is approved, CODEION will generally initiate the refund
          within 3 to 7 business days.
        </p>

        <p>
          After initiation, the time required for funds to appear in the user's
          bank account, card, wallet, or other payment method may depend on the
          payment provider and financial institution.
        </p>
      </Section>

      <Section id="duplicate" number="06" title="Duplicate or Excess Payments">
        <p>
          If you believe you were charged more than once for the same enrollment,
          contact CODEION support with the relevant transaction details.
        </p>

        <p>
          Verified duplicate or excess payments will be reviewed separately and
          may be refunded to the original payment method.
        </p>
      </Section>

      <Section id="cancellation" number="07" title="Program Cancellation by CODEION">
        <p>
          If CODEION cancels a paid program and does not provide a reasonable
          alternative, transfer, credit, rescheduling option, or equivalent
          service, affected learners may be offered an appropriate refund based
          on the circumstances.
        </p>

        <p>
          Temporary schedule changes, instructor substitutions, reasonable batch
          rescheduling, or updates to course delivery do not automatically qualify
          an enrollment for a refund.
        </p>
      </Section>

      <Section id="discounts" number="08" title="Promotional & Discounted Enrollments">
        <p>
          Enrollments purchased using discounts, referral benefits, scholarships,
          coupons, promotional pricing, or special offers remain subject to this
          Refund Policy unless different refund conditions were clearly disclosed
          for the specific offer before purchase.
        </p>
      </Section>

      <Section id="chargebacks" number="09" title="Payment Disputes & Chargebacks">
        <p>
          Users are encouraged to contact CODEION support before initiating a
          payment dispute or chargeback so that payment, enrollment, and refund
          concerns can be investigated.
        </p>

        <p>
          Fraudulent chargebacks, false claims, or misuse of payment dispute
          procedures may result in suspension of access while the matter is
          investigated.
        </p>
      </Section>

      <Section id="decision" number="10" title="Review & Final Decision">
        <p>
          CODEION may review payment records, enrollment details, class attendance,
          content access, support history, and other relevant information when
          determining refund eligibility.
        </p>

        <p>
          Refund decisions will be made in accordance with this Policy and
          applicable law. Nothing in this Policy limits consumer rights that
          cannot legally be excluded.
        </p>
      </Section>

      <Section id="contact" number="11" title="Contact Us">
        <p>To submit a refund request or ask a refund-related question, contact:</p>

        <p>
          Email:{" "}
          <a href="mailto:support@codeion.in">
            support@codeion.in
          </a>
        </p>

        <p>
          Support hours: Monday to Saturday, 10:00 AM to 7:00 PM IST.
        </p>
      </Section>

      <div className="legal-contact-card">
        <h2>Need help with a payment or refund?</h2>

        <p>
          Send your enrollment and transaction details to the CODEION support
          team so your request can be reviewed.
        </p>

        <a
          className="legal-contact-button"
          href="mailto:support@codeion.in?subject=CODEION%20Refund%20Request"
        >
          Request Refund Support →
        </a>
      </div>
    </LegalLayout>
  );
}

export default RefundPolicy;